import React, { createRef, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";

import styles from "assets/jss/material-dashboard-pro-react/components/storeHours.js";
import { useDispatch, useSelector } from "react-redux";
import RegularButton from "../../components/CustomButtons/Button";
import { FormControlLabel, Switch, TextField } from "@material-ui/core";
import moment from "moment";
import classNames from "classnames";
import { updateStoreHours } from "../../store/app/actions";
import Overlay from "../../components/Overlay/Overlay";
import { Prompt } from "react-router-dom";

const daysInOrder = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const daysToLongForm = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const useStyles = makeStyles(styles);

const OverrideHours = ({ overrides, setSuccess, setError, tzOffset }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app.user);
  const isAdmin = useMemo(() => {
    return user?.type === "saucey_admin";
  }, [user?.type]);

  const rowRefs = useRef((overrides || []).map(() => createRef()));
  const [normalizedOverrides, setNormalizedOverrides] = useState([]);
  const [amDirty, setAmDirty] = useState(false);

  useEffect(() => {
    let _overrides = [];
    if (overrides) {
      _overrides = overrides.map((override) => {
        const hourSplit = override.hours.split("-");
        const opensAt = moment(hourSplit[0], "HHmm")
          .add(tzOffset * -1, "minutes")
          .format("HH:mm");
        const closesAt = moment(hourSplit[1], "HHmm")
          .add(tzOffset * -1, "minutes")
          .format("HH:mm");
        const date = moment(override.date, "MM/DD/YYYY").format("YYYY-MM-DD");
        return {
          ...override,
          date,
          opensAt,
          closesAt,
        };
      });
      setNormalizedOverrides(_overrides);
    }
  }, [JSON.stringify(overrides)]);

  const changeOpenFlag = (idx, event) => {
    let hoursCloned = _.cloneDeep(normalizedOverrides);
    hoursCloned[idx].open = event.target.checked;
    setNormalizedOverrides(hoursCloned);
    hoursCloned[idx].error = null;
    setAmDirty(true);
  };

  const changeTime = (idx, which, event) => {
    let hoursCloned = _.cloneDeep(normalizedOverrides);
    hoursCloned[idx][which] = event.target.value;
    hoursCloned[idx].error = null;
    setNormalizedOverrides(hoursCloned);
    setAmDirty(true);
  };

  const addDate = () => {
    let hoursCloned = _.cloneDeep(normalizedOverrides);
    hoursCloned.push({
      open: true,
      opensAt: "00:00",
      closesAt: "00:00",
      hours: "0000-2400",
      date: "",
      name: "",
    });
    rowRefs.current.push(createRef());
    setNormalizedOverrides(hoursCloned);
    setAmDirty(true);
  };
  const removeDate = (idx) => {
    let hoursCloned = _.cloneDeep(normalizedOverrides);
    hoursCloned.splice(idx, 1);
    rowRefs.current.splice(idx, 1);
    setNormalizedOverrides(hoursCloned);
    setAmDirty(true);
  };
  const setName = (idx, event) => {
    let hoursCloned = _.cloneDeep(normalizedOverrides);
    hoursCloned[idx].name = event.currentTarget.value;
    hoursCloned[idx].error = null;
    setNormalizedOverrides(hoursCloned);
    setAmDirty(true);
  };
  const changeDate = (idx, event) => {
    let hoursCloned = _.cloneDeep(normalizedOverrides);
    hoursCloned[idx].date = event.currentTarget.value;
    hoursCloned[idx].error = null;
    setNormalizedOverrides(hoursCloned);
    setAmDirty(true);
  };
  const save = () => {
    let hoursCloned = _.cloneDeep(normalizedOverrides);

    let hasError = false;
    let firstErrorIdx = 99999;
    let toSubmit = normalizedOverrides.map((override, idx) => {
      let closesAt = moment(override.closesAt, "HH:mm")
        .add(tzOffset, "minutes")
        .format("HHmm");
      if (!override.name) {
        hoursCloned[idx].error = "Name must not be blank";
        hasError = true;
        firstErrorIdx = Math.min(idx, firstErrorIdx);
        return override;
      }
      if (!override.date) {
        hoursCloned[idx].error = "Date must not be blank";
        hasError = true;
        firstErrorIdx = Math.min(idx, firstErrorIdx);
        return override;
      }
      if (override.closesAt === "00:00") {
        closesAt = "2400";
      } else {
        if (
          override.open &&
          moment(override.opensAt, "HH:mm").isAfter(
            moment(override.closesAt, "HH:mm")
          )
        ) {
          hoursCloned[idx].error = "Closing time must be after opening time";
          hasError = true;
          firstErrorIdx = Math.min(idx, firstErrorIdx);
          return override;
        }
      }
      override.hours = `${moment(override.opensAt, "HH:mm")
        .add(tzOffset, "minutes")
        .format("HHmm")}-${closesAt}`;

      return override;
    });

    toSubmit = toSubmit.map((override) => {
      return {
        name: override.name,
        date: moment(override.date, "YYYY-MM-DD").format("MM/DD/YYYY"),
        open: override.open,
        hours: override.hours,
      };
    });

    if (hasError) {
      setNormalizedOverrides(hoursCloned);
      rowRefs.current[firstErrorIdx]?.current?.scrollIntoView({
        behavior: "smooth",
      });
      return;
    }

    dispatch(updateStoreHours({ special_hours: toSubmit }))
      .then(() => {
        setSuccess(true);
        setAmDirty(false);
      })
      .catch(() => setError(true));
  };

  return (
    <div>
      <Prompt
        message={"You have unsaved changes, confirm exiting?"}
        when={amDirty}
      />
      <div className={classes.sectionHeader}>
        <h4>Holiday Special Hours</h4>
        <div className={classes.sectionHeaderButtonContainer}>
          {isAdmin && (
            <RegularButton onClick={addDate} color="info">
              Add date
            </RegularButton>
          )}
          <RegularButton color="primary" onClick={save} disabled={!amDirty}>
            Save
          </RegularButton>
        </div>
      </div>

      {normalizedOverrides.map((override, idx) => {
        return (
          <div
            ref={rowRefs.current[idx]}
            className={classNames({
              [classes.dayContainer]: true,
              [classes.dayContainerError]: override.error,
            })}
            key={`regular_hour_${idx}`}
          >
            <div className={classes.error}>{override.error}</div>
            <div className={classes.dayHeader}>
              <div className={classes.dayName}>
                <TextField
                  disabled={!isAdmin}
                  label="Name"
                  type="text"
                  value={override.name}
                  className={classes.textField}
                  style={{ width: 250 }}
                  onChange={(e) => setName(idx, e)}
                />
              </div>
              {isAdmin && (
                <div className={classes.openSwitchContainer}>
                  <RegularButton
                    onClick={() => removeDate(idx)}
                    color={"info"}
                    simple
                  >
                    Remove Date
                  </RegularButton>
                </div>
              )}
            </div>
            <div className={classes.dayHeader}>
              <div className={classes.datePickersContainer}>
                <TextField
                  disabled={!isAdmin}
                  label="Date"
                  type="date"
                  value={override.date}
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ width: 150 }}
                  onChange={(e) => changeDate(idx, e)}
                />
              </div>
              <div className={classes.openSwitchContainer}>
                <FormControlLabel
                  value="bottom"
                  control={
                    <Switch
                      checked={override.open}
                      onChange={(e) => changeOpenFlag(idx, e)}
                      classes={{
                        switchBase: classes.switchBase,
                        checked: classes.switchChecked,
                        thumb: classes.switchIcon,
                        track: classes.switchBar,
                      }}
                    />
                  }
                  label={override.open ? "Open" : "Closed"}
                  labelPlacement="bottom"
                />
              </div>
            </div>
            {override.open && (
              <div className={classes.dayHeader}>
                <div className={classes.datePickersContainer}>
                  <TextField
                    id="time"
                    label="Opens at"
                    type="time"
                    value={override.opensAt}
                    className={classes.textField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    style={{ width: 150 }}
                    onChange={(e) => changeTime(idx, "opensAt", e)}
                  />
                  {"-"}
                  <TextField
                    id="time"
                    label={`Closes at`}
                    type="time"
                    value={override.closesAt}
                    className={classes.textField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    style={{ width: 150 }}
                    onChange={(e) => changeTime(idx, "closesAt", e)}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const RegularHours = ({ hours, setSuccess, setError, tzOffset, activeStore }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app.user);
  const [amDirty, setAmDirty] = useState(false);

  const isAdmin = useMemo(() => {
    return user?.type === "saucey_admin";
  }, [user?.type]);

  const [normalizedHours, setNormalizedHours] = useState([]);

  useEffect(() => {
    if (hours) {
      let hoursInArray = [];
      daysInOrder.forEach((day, idx) => {
        hoursInArray[idx] = hours[day].replace(" ", "");
      });

      hoursInArray.forEach((todayHours, idx) => {
        todayHours = todayHours.split(",");
        let idxToRemove = -1;
        todayHours.forEach((oneBlock, idx2) => {
          const hourSplit = oneBlock.split("-");
          const open = hourSplit[0];
          const close = hourSplit[1];

          if (open === "0000" && todayHours.length > 1) {
            let mergeWithIndex = idx - 1;
            if (mergeWithIndex === -1) {
              mergeWithIndex = 6;
            }

            const hoursToBeMerged = hoursInArray[mergeWithIndex].split(",");
            hoursToBeMerged.forEach((hourBlockToBeMerged, idx3) => {
              const hourSplit = hourBlockToBeMerged.split("-");
              let toBeMergedClose = hourSplit[1];
              if (toBeMergedClose === "2400") {
                hoursToBeMerged[idx3] = [hourSplit[0], close].join("-");
                hoursInArray[mergeWithIndex] = hoursToBeMerged.join(",");
              }
            });
            idxToRemove = idx2;
          }
        });
        if (idxToRemove !== -1) {
          todayHours.splice(idxToRemove, 1);
        }
        hoursInArray[idx] = todayHours[0];
      });
      for (let i = 0; i < 7; i++) {
        if (hoursInArray[i] === undefined || hoursInArray[i] === "" || hoursInArray[i] === "0000-0000") {
          hoursInArray[i] = {
            open: false,
            opensAt: "12:00am",
            closesAt: "12:00am",
            closesNextDay: true,
            allDay: true,
          };
        } else {
          const hourSplit = hoursInArray[i].split("-");
          const opensAt = moment(hourSplit[0], "HHmm")
            .add(tzOffset * -1, "minutes")
            .format("HH:mm");
          const closesAt = moment(hourSplit[1], "HHmm")
            .add(tzOffset * -1, "minutes")
            .format("HH:mm");
          hoursInArray[i] = {
            open: true,
            opensAt,
            closesAt,
            closesNextDay: moment(opensAt, "HH:mm").isAfter(
              moment(closesAt, "HH:mm")
            ),
            allDay: opensAt === closesAt,
          };
        }
      }
      setNormalizedHours(hoursInArray);
    }
  }, [JSON.stringify(hours)]);

  const save = () => {
    let toSubmit = {};
    normalizedHours.forEach((hours, idx) => {
      if (!toSubmit[daysInOrder[idx]]) {
        toSubmit[daysInOrder[idx]] = [];
      }
      if (!hours.open) {
        return;
      }
      if (hours.allDay) {
        toSubmit[daysInOrder[idx]].push("0000-2400");
      } else {
        if (hours.closesNextDay) {
          toSubmit[daysInOrder[idx]].push(
            `${moment(hours.opensAt, "HH:mm")
              .add(tzOffset, "minutes")
              .format("HHmm")}-2400`
          );
          let nextDay = idx === 6 ? 0 : idx + 1;
          if (!toSubmit[daysInOrder[nextDay]]) {
            toSubmit[daysInOrder[nextDay]] = [];
          }
          toSubmit[daysInOrder[nextDay]].unshift(
            `0000-${moment(hours.closesAt, "HH:mm")
              .add(tzOffset, "minutes")
              .format("HHmm")}`
          );
        } else {
          toSubmit[daysInOrder[idx]].push(
            `${moment(hours.opensAt, "HH:mm")
              .add(tzOffset, "minutes")
              .format("HHmm")}-${moment(hours.closesAt, "HH:mm")
              .add(tzOffset, "minutes")
              .format("HHmm")}`
          );
        }
      }
    });
    Object.keys(toSubmit).forEach((key) => {
      toSubmit[key] = toSubmit[key].join(",");
    });

    dispatch(updateStoreHours({ hours: toSubmit }))
      .then(() => {
        setSuccess(true);
        setAmDirty(false);
      })
      .catch(() => setError(true));
  };

  const saveButtonHandler = () => {
    if (!isAdmin) {
      window.open(`https://docs.google.com/forms/d/e/1FAIpQLSew8jXexyxF_fZNnkIlb9hJX4HHy70OzwVZUE550nQpgT1wIw/viewform?usp=pp_url&entry.1737665624=${activeStore?.store_id}&entry.1315802488=${activeStore?.name}`)
    } else {
      save();
    }
  };

  const changeOpenFlag = (idx, event) => {
    let hoursCloned = _.cloneDeep(normalizedHours);
    hoursCloned[idx].open = event.target.checked;
    setNormalizedHours(hoursCloned);
    setAmDirty(true);
  };
  const changeTime = (idx, which, event) => {
    let hoursCloned = _.cloneDeep(normalizedHours);
    hoursCloned[idx][which] = event.target.value;
    hoursCloned[idx].closesNextDay = moment(
      hoursCloned[idx].opensAt,
      "HH:mm"
    ).isAfter(moment(hoursCloned[idx].closesAt, "HH:mm"));

    hoursCloned[idx].allDay =
      hoursCloned[idx].opensAt === hoursCloned[idx].closesAt;
    setNormalizedHours(hoursCloned);
    setAmDirty(true);
  };

  const changeAllDayFlag = (idx, event) => {
    let hoursCloned = _.cloneDeep(normalizedHours);
    hoursCloned[idx].allDay = event.target.checked;
    setNormalizedHours(hoursCloned);
    setAmDirty(true);
  };

  return (
    <div>
      <Prompt
        message={"You have unsaved changes, confirm exiting?"}
        when={amDirty}
      />
      <div className={classes.sectionHeader}>
        <h4>Store Hours</h4>
        <div style={{ marginLeft: 10 }} />
        {!isAdmin && (
          <h5>
            For emergency closure, please contact live ops at 323-676-1551
          </h5>
        )}
        <div className={classes.sectionHeaderButtonContainer}>
          {isAdmin ? (
            <RegularButton
              disabled={!amDirty}
              onClick={saveButtonHandler}
              color="primary"
            >
              save
            </RegularButton>
          ) : (
            <RegularButton onClick={saveButtonHandler} color="primary">
              request a change
            </RegularButton>
          )}
        </div>
      </div>
      {[0, 1, 2, 3, 4, 5, 6].map((idx) => {
        if (!normalizedHours[idx]) return null;
        return (
          <div className={classes.dayContainer} key={`regular_hour_${idx}`}>
            <div className={classes.dayHeader}>
              <div className={classes.dayName}>
                <h5 style={{ width: 80 }}>{daysToLongForm[idx]}</h5>{" "}
                {normalizedHours[idx].open && (
                  <FormControlLabel
                    value="bottom"
                    control={
                      <Switch
                        disabled={!isAdmin}
                        checked={normalizedHours[idx].allDay}
                        onChange={(e) => changeAllDayFlag(idx, e)}
                        classes={{
                          switchBase: classes.switchBase,
                          checked: classes.switchChecked,
                          thumb: classes.switchIcon,
                          track: classes.switchBar,
                        }}
                      />
                    }
                    label="24 Hours"
                    labelPlacement="end"
                  />
                )}
              </div>
              <div className={classes.openSwitchContainer}>
                <FormControlLabel
                  value="bottom"
                  control={
                    <Switch
                      disabled={!isAdmin}
                      checked={normalizedHours[idx].open}
                      onChange={(e) => changeOpenFlag(idx, e)}
                      classes={{
                        switchBase: classes.switchBase,
                        checked: classes.switchChecked,
                        thumb: classes.switchIcon,
                        track: classes.switchBar,
                      }}
                    />
                  }
                  label={normalizedHours[idx].open ? "Open" : "Closed"}
                  labelPlacement="bottom"
                />
              </div>
            </div>
            {normalizedHours[idx].open && !normalizedHours[idx].allDay && (
              <div className={classes.datePickersContainer}>
                <TextField
                  disabled={normalizedHours[idx].allDay || !isAdmin}
                  id="time"
                  label="Opens at"
                  type="time"
                  value={normalizedHours[idx].opensAt}
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ width: 150 }}
                  onChange={(e) => changeTime(idx, "opensAt", e)}
                />
                {"-"}
                <TextField
                  disabled={normalizedHours[idx].allDay || !isAdmin}
                  id="time"
                  label={`Closes at${
                    normalizedHours[idx].closesNextDay ? " (Next Day)" : ""
                  }`}
                  type="time"
                  value={
                    normalizedHours[idx].allDay
                      ? normalizedHours[idx].opensAt
                      : normalizedHours[idx].closesAt
                  }
                  className={classes.textField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ width: 150 }}
                  onChange={(e) => changeTime(idx, "closesAt", e)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const StoreHours = () => {
  const activeStore = useSelector((state) => state.app.activeStoreObject);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const tzOffset = useMemo(() => {
    if (activeStore?.tz) {
      const pstOffset = moment().tz("America/Los_Angeles").utcOffset();
      const sellerOffset = moment().tz(activeStore.tz).utcOffset();
      return pstOffset - sellerOffset;
    }
    return 0;
  }, [activeStore?.tz]);

  useEffect(() => {
    if (success) {
      setTimeout(() => setSuccess(false), 1500);
    }
  }, [success]);
  const classes = useStyles();
  return (
    <div className={classes.storeHourMainContainer}>
      {success && (
        <Overlay
          style={{ fontSize: 96 }}
          type="success"
          message={"Store hour saved successfully"}
          dismiss={
            <RegularButton
              simple
              color={"primary"}
              onClick={() => setSuccess(false)}
            >
              OK
            </RegularButton>
          }
        />
      )}
      {error && (
        <Overlay
          style={{ fontSize: 96 }}
          type="error"
          message={"Error saving store hours"}
          dismiss={
            <RegularButton
              simple
              color={"primary"}
              onClick={() => setError(false)}
            >
              OK
            </RegularButton>
          }
        />
      )}

      <RegularHours
        hours={_.cloneDeep(activeStore?.hours)}
        setSuccess={setSuccess}
        setError={setError}
        tzOffset={tzOffset}
        activeStore={activeStore  }
      />
      <OverrideHours
        overrides={_.cloneDeep(activeStore?.special_hours)}
        setSuccess={setSuccess}
        setError={setError}
        tzOffset={tzOffset}
      />
    </div>
  );
};

export default StoreHours;
